import {
  CartItemOnlineFulfillmentEnum,
  FulfillmentPreference,
  UserFulfillmentMode
} from 'types';

export interface CartItemWithFulfillment {
  cart_item_id: number;
  online_fulfillment?: CartItemOnlineFulfillmentEnum;
  name?: string;
}

/**
 * Determines if cart has mixed fulfillment scenario (PICKUP ONLY + SHIP ONLY)
 */
export const hasMixedFulfillment = (
  items: CartItemWithFulfillment[]
): boolean => {
  if (!items || items.length === 0) return false;

  const fulfillmentTypes = new Set(
    items.map((item) => item.online_fulfillment)
  );

  const hasPickupOnly = fulfillmentTypes.has(
    CartItemOnlineFulfillmentEnum.PickupOnly
  );
  const hasShippingOnly = fulfillmentTypes.has(
    CartItemOnlineFulfillmentEnum.ShippingOnly
  );

  return hasPickupOnly && hasShippingOnly;
};

/**
 * Gets the effective fulfillment mode for an item considering user preferences
 */
export const getEffectiveFulfillmentMode = (
  item: CartItemWithFulfillment,
  userPreferences: { [cart_item_id: number]: FulfillmentPreference }
): UserFulfillmentMode => {
  const userPref = userPreferences[item.cart_item_id];

  if (userPref) {
    return userPref.user_selected;
  }
  switch (item.online_fulfillment) {
    case CartItemOnlineFulfillmentEnum.PickupOnly:
      return UserFulfillmentMode.Pickup;
    case CartItemOnlineFulfillmentEnum.ShippingOnly:
      return UserFulfillmentMode.Ship;
    case CartItemOnlineFulfillmentEnum.ShippingAndPickup:
    default:
      return UserFulfillmentMode.Ship;
  }
};

/**
 * Separates cart items by their optimal fulfillment method, considering flow optimization
 * This version may override user preferences to avoid artificial mixed flows
 */
export const separateItemsByOptimalFulfillment = (
  items: CartItemWithFulfillment[],
  userPreferences: { [cart_item_id: number]: FulfillmentPreference }
): {
  pickupItems: number[];
  shippingItems: number[];
  conflictItems: number[];
} => {
  const pickupOnlyItems = items.filter(
    (item) =>
      item.online_fulfillment === CartItemOnlineFulfillmentEnum.PickupOnly
  );
  const shippingOnlyItems = items.filter(
    (item) =>
      item.online_fulfillment === CartItemOnlineFulfillmentEnum.ShippingOnly
  );
  const bothOptionsItems = items.filter(
    (item) =>
      item.online_fulfillment ===
      CartItemOnlineFulfillmentEnum.ShippingAndPickup
  );

  const hasMandatoryPickup = pickupOnlyItems.length > 0;
  const hasMandatoryShipping = shippingOnlyItems.length > 0;
  const isTrueMixedFlow = hasMandatoryPickup && hasMandatoryShipping;

  const pickupItems: number[] = [];
  const shippingItems: number[] = [];
  const conflictItems: number[] = [];

  // Always include mandatory items
  pickupOnlyItems.forEach((item) => pickupItems.push(item.cart_item_id));
  shippingOnlyItems.forEach((item) => shippingItems.push(item.cart_item_id));

  // Handle flexible items based on flow type
  bothOptionsItems.forEach((item) => {
    if (isTrueMixedFlow) {
      // In true mixed flow, respect user preferences
      const effectiveMode = getEffectiveFulfillmentMode(item, userPreferences);
      if (effectiveMode === UserFulfillmentMode.Pickup) {
        pickupItems.push(item.cart_item_id);
      } else {
        shippingItems.push(item.cart_item_id);
      }
    } else if (hasMandatoryPickup && !hasMandatoryShipping) {
      // Force all flexible items to pickup to match mandatory pickup items
      pickupItems.push(item.cart_item_id);
    } else if (!hasMandatoryPickup && hasMandatoryShipping) {
      // Force all flexible items to shipping to match mandatory shipping items
      shippingItems.push(item.cart_item_id);
    } else {
      // Only flexible items - respect user preferences or default to shipping
      const effectiveMode = getEffectiveFulfillmentMode(item, userPreferences);
      if (effectiveMode === UserFulfillmentMode.Pickup) {
        pickupItems.push(item.cart_item_id);
      } else {
        shippingItems.push(item.cart_item_id);
      }
    }
  });

  return { pickupItems, shippingItems, conflictItems };
};
